/* eslint-disable complexity */
import { Component, Input } from '@angular/core';
import {
  DynamicBannerConfiguration,
  DynamicBannerContent,
  DynamicBannerStyleProperties
} from '../../../models/base-properties';
import { CSSProperties } from '../../../models/styles.interfaces';
import { BaseProperties } from '../../../models/ui.interfaces';
import {
  BannerHeader,
  BannerRow
} from '../banner-alert/models/banner-alert.interface';

/**
 * This component is responsible to display slim banner
 * (image with heading, description and more info)
 */
@Component({
  selector: 'idp-banner-slim',
  templateUrl: './banner-slim.component.html',
  styleUrls: ['./banner-slim.component.scss']
})
export class SharedBannerSlimComponent
  implements
    BaseProperties<
      DynamicBannerConfiguration,
      DynamicBannerContent,
      DynamicBannerStyleProperties
    >
{
  /** This props holds all content regarding slim image banner */
  @Input() content: DynamicBannerContent = {} as DynamicBannerContent;
  /**
   * This is the primary configuration for slim image banner.
   * You can pass several configuration like device configuration and template configuration
   */
  @Input() config: DynamicBannerConfiguration =
    {} as DynamicBannerConfiguration;

  /** This holds style properties. You can set apply styling according to the use case */
  @Input() styles: DynamicBannerStyleProperties =
    {} as DynamicBannerStyleProperties;

  constructor() {
    /** Set default properties for config, content and styles */
    this.initializeContent();
    this.initializeConfig();
    this.initializeStyles();
  }

  /** Initialize content regarding this component */
  private initializeContent() {
    this.content = {
      imageContent: {
        desktop: { src: '', alt: '' },
        mobile: { src: '', alt: '' }
      }
    };
  }

  /** Initialize config regarding this component */
  private initializeConfig() {
    this.config = {
      deviceConfig: {
        desktopEnabled: true,
        mobileEnabled: true,
        responsive: true
      },
      templateType: 'slimbanner'
    };
  }

  /** Initialize style regarding this component */
  private initializeStyles() {
    this.styles = {
      banner: { backgroundColor: '', borderColor: '' },
      heading: { color: '', position: 'left' },
      description: {
        color: '',
        fontSize: '',
        position: 'left'
      },
      textPosition: {
        position: 'left'
      }
    };
  }

  /** Prepare dynamic class for which the banner can be shift to left or right based on the position */
  get bannerRowClass(): BannerRow {
    let flexRow = false;
    let bannerBorder = false;
    if (
      this.styles.textPosition &&
      this.styles.textPosition.position === 'left'
    ) {
      flexRow = true;
    }

    if (this.styles.banner && this.styles.banner.borderColor) {
      bannerBorder = true;
    }
    return { flexRowReverse: flexRow, hasBorder: bannerBorder };
  }

  /** Check desktop image setting is enabled of not */
  get desktopBannerEnabled(): boolean {
    let desktopImageEnabled = false;
    if (
      this.config.deviceConfig.desktopEnabled &&
      this.content.imageContent?.desktop?.src
    ) {
      desktopImageEnabled = true;
    }
    return desktopImageEnabled;
  }

  /**  check mobile image setting is enabled of not */
  get mobileBannerEnabled(): boolean {
    return this.config.deviceConfig.mobileEnabled &&
      this.content.imageContent?.mobile?.src
      ? true
      : false;
  }

  /**  prepare header styling according to the inputs */
  get headerStyles(): CSSProperties {
    return {
      color: this.styles.heading?.color || '',
      position: this.styles.heading?.position || 'left'
    };
  }

  /**  prepare header content for full image banner */
  get headerContent(): BannerHeader {
    return {
      text: this.content.headerText || '',
      level: this.content.level || ''
    };
  }
}
